export type Drawing =
  | { id: string; type: 'hline'; price: number; color?: string; title?: string }
  | {
      id: string;
      type: 'range';
      priceLow: number;
      priceHigh: number;
      timeFrom?: number | null;
      timeTo?: number | null;
    }
  | { id: string; type: 'trend'; t1: number; p1: number; t2: number; p2: number; color?: string };

/** Chart hint overlays — visual only, never drive AI Chips. */
export type ChartIndicators = {
  sma?: boolean;
  ema?: boolean;
  rsi?: boolean;
  volume?: boolean;
  /** Seconds left until current candle closes — also a live heartbeat. */
  barCountdown?: boolean;
  smaLen?: number;
  emaLen?: number;
  rsiLen?: number;
  /** RSI overbought / oversold guide bands on the RSI pane. */
  rsiZones?: boolean;
  rsiOb?: number;
  rsiOs?: number;
  /** Auto horizontal levels from swing clusters. */
  autoLevels?: boolean;
  autoLevelsCount?: number;
  /** Auto trend lines from swing highs/lows. */
  autoTrends?: boolean;
  autoTrendsCount?: number;
  /** Lightweight order-block rectangles (hint only). */
  orderBlocks?: boolean;
  orderBlocksCount?: number;
  /**
   * How L2 book affects OB boxes while OB is on (live redraw on depth poll):
   * - `annotate` — classic candle-body zone; book qty/levels only in the label
   * - `widen` — snap zone edges to nearby real book walls (capped)
   */
  orderBlocksBook?: 'annotate' | 'widen';
  /** Mitigated OBs as LuxAlgo breakers (flipped polarity). Separate from live OB. */
  obBreakers?: boolean;
  obBreakersCount?: number;
  /** 0=strict / few · 100=loose / many — shared sensitivity for structure hints. */
  hintSensitivity?: number;
  /**
   * Where RSI lives: separate pane (TradingView default) or overlaid on the price pane.
   * Drag the RSI chip onto the main chart to set `main`.
   */
  rsiPane?: 'separate' | 'main';
  /** Volume histogram: overlaid on price (`main`) or its own pane. */
  volumePane?: 'separate' | 'main';
};

export type ChartPayload = {
  drawings: Drawing[];
  indicators?: ChartIndicators;
};

export const newId = () => crypto.randomUUID();

export const emptyPayload = (): ChartPayload => ({
  drawings: [],
  indicators: {
    sma: true,
    ema: false,
    rsi: true,
    volume: true,
    barCountdown: true,
    smaLen: 20,
    emaLen: 50,
    rsiLen: 14,
    rsiZones: false,
    rsiOb: 70,
    rsiOs: 30,
    autoLevels: false,
    autoLevelsCount: 6,
    autoTrends: false,
    autoTrendsCount: 2,
    orderBlocks: false,
    orderBlocksCount: 5,
    orderBlocksBook: 'annotate',
    obBreakers: false,
    obBreakersCount: 4,
    hintSensitivity: 50,
    rsiPane: 'separate',
    volumePane: 'main',
  },
});
